<script setup lang="ts">
import { computed, ref } from 'vue'
import { useDateTimePicker } from '../../composables/DateTimePickerState'
import { PBtnType } from '../../../btn/types'
import PIcon from '../../../icon/PIcon.vue'
import PSpace from '../../../space/PSpace.vue'
import { PSpaceSize } from '../../../space/types'
import { usePrevioDS } from '../../../../composables/helpers'
import YearNavigation from './components/YearNavigation.vue'
import MonthNavigation from './components/MonthNavigation.vue'
import CalendarNavigationItem from './components/CalendarNavigationItem.vue'

type Emits = {
  (e: 'subtract'): void
  (e: 'advance'): void
}

const emits = defineEmits<Emits>()

const ds = usePrevioDS()
const navigation = useDateTimePicker()

const desktopCurrentCalendarMonthNavigationComponent = ref<InstanceType<typeof MonthNavigation>>()
const desktopSecondCalendarMonthNavigationComponent = ref<InstanceType<typeof MonthNavigation>>()
const desktopCurrentCalendarYearNavigationComponent = ref<InstanceType<typeof YearNavigation>>()
const desktopSecondCalendarYearNavigationComponent = ref<InstanceType<typeof YearNavigation>>()

const mobileMonthNavigationComponent = ref<InstanceType<typeof MonthNavigation>>()
const mobileYearNavigationComponent = ref<InstanceType<typeof YearNavigation>>()

const isLeftHandleConstrained = computed<boolean>(() => {
  if (!navigation.getMin()) {
    return false
  }

  const prevMonth = navigation.currentCalendar().subtract(1, 'month')

  return prevMonth.isBefore(navigation.getMin(), 'month')
})

const isRightHandleConstrained = computed<boolean>(() => {
  if (!navigation.getMax()) {
    return false
  }

  const nextMonth = navigation.currentCalendar().add(1, 'month')

  return nextMonth.isAfter(navigation.getMax(), 'month')
})

const onLeftHandleClick = (): void => {
  if (isLeftHandleConstrained.value) {
    return
  }

  navigation.subtractMonth()
  emits('subtract')
}

const onRightHandleClick = (): void => {
  if (isRightHandleConstrained.value) {
    return
  }

  navigation.advanceMonth()
  emits('advance')
}
</script>

<template>
  <div class="ds-date-time-picker__navigation p-flex p-items-center p-justify-between">
    <p-icon
      :type="PBtnType.TERTIARY"
      :class="{ 'ds-date-time-picker__navigation--constrained': isLeftHandleConstrained }"
      icon="chevron-left"
      cy="date-time-picker-navigation-arrow-left"
      cursor-pointer
      @click="onLeftHandleClick"
    />
    <p-space :mobile="PSpaceSize.MD" />

    <div class="p-flex p-items-center">
      <template v-if="ds.screen().isLaptopAndLarge">
        <div class="p-flex p-flex-center">
          <calendar-navigation-item
            :text="navigation.currentMonthFormatted()"
            :value="navigation.currentCalendar().month()"
            :navigation-component="desktopCurrentCalendarMonthNavigationComponent"
          />

          <month-navigation ref="desktopCurrentCalendarMonthNavigationComponent" />

          <p-space :mobile="PSpaceSize.XS" />

          <calendar-navigation-item
            :text="navigation.currentCalendar().year()"
            :value="navigation.currentCalendar().year()"
            :navigation-component="desktopCurrentCalendarYearNavigationComponent"
          />

          <year-navigation ref="desktopCurrentCalendarYearNavigationComponent" />
        </div>

        <p-space :mobile="PSpaceSize.XXL" />
        <p-space :mobile="PSpaceSize.XXL" />

        <div class="p-flex p-flex-center">
          <calendar-navigation-item
            :text="navigation.nextMonthFormatted()"
            :value="navigation.secondCalendar().month()"
            :navigation-component="desktopSecondCalendarMonthNavigationComponent"
            :offset="1"
          />

          <month-navigation ref="desktopSecondCalendarMonthNavigationComponent" />

          <p-space :mobile="PSpaceSize.XS" />

          <calendar-navigation-item
            :text="navigation.secondCalendar().year()"
            :value="navigation.secondCalendar().year()"
            :navigation-component="desktopSecondCalendarYearNavigationComponent"
          />

          <year-navigation ref="desktopSecondCalendarYearNavigationComponent" />
        </div>
      </template>

      <template v-else>
        <calendar-navigation-item
          :text="navigation.currentMonthFormatted()"
          :value="navigation.currentCalendar().month()"
          :navigation-component="mobileMonthNavigationComponent"
        />

        <month-navigation ref="mobileMonthNavigationComponent" />

        <p-space :mobile="PSpaceSize.SM" />

        <calendar-navigation-item
          :text="navigation.currentCalendar().year()"
          :value="navigation.currentCalendar().year()"
          :navigation-component="mobileYearNavigationComponent"
        />

        <year-navigation ref="mobileYearNavigationComponent" />
      </template>
    </div>

    <p-space :mobile="PSpaceSize.MD" />
    <p-icon
      :type="PBtnType.TERTIARY"
      :class="{ 'ds-date-time-picker__navigation--constrained': isRightHandleConstrained }"
      icon="chevron-right"
      cy="date-time-picker-navigation-arrow-right"
      cursor-pointer
      @click="onRightHandleClick"
    />
  </div>
</template>

<style lang="scss" scoped>
@import '../../../../assets/scss/shared/brand.module';

.ds-date-time-picker {
  &__navigation {
    margin: 12px;

    &--constrained {
      color: getColor('grey400');
      cursor: not-allowed;
    }
  }
}
</style>
